// src/api/sdhAPI.ts — Solid-Dependency-Hardening UI API.
// Powers the new DQ pages: SME Review, Approved Rules, Results, Data Profile, Export.
// Same conventions as ekdAPI/bcAPI: thin apiGet/apiPost wrappers over /adriva_api/dq/sdh.
import { apiGet, apiPost, api as apiClient } from "../API";

const BASE = "/adriva_api/dq/sdh";

export type Lifecycle = "All" | "Approved" | "Draft" | "Rejected";

export interface ReviewItem {
  id?: string; decision_type: string; title: string;
  fields?: string[]; current_disposition?: string;
  recommended_action?: string; options?: string[];
  evidence?: any; rule?: any; question?: string; reason?: string;
  control_id?: string; [k: string]: any;
}
export interface ReviewQueue {
  success: boolean; entity_id: string; total: number;
  by_type: Record<string, number>; items: ReviewItem[];
}

export interface EnrichedRule {
  rule_id: string; rule_name: string; family: string; owner_field?: string; fields?: string;
  description?: string; business_logic?: string; severity?: string;
  approval_status?: string; approved_by?: string; approval_basis?: string;
  rule_logic_authority?: string; business_control_id?: string;
  citation?: any; policy_citation?: any;
  impact_violations?: number | null; impact_applicable_rows?: number | null;
  impact_pass_rate?: number | null; impact_status?: string | null;
  remediation?: string; evidence_snapshot_id?: string; execution_scope?: string;
  // Phase-1 versioning + SME feedback (written by rule_versioning + sme_writeback)
  version?: number; content_hash?: string; review_status?: string;
  sme_decision?: { choice?: string; assessment?: string; decision_gate?: string; comment?: string;
                   sme_id?: string; decided_at?: string; reviewed_version?: number };
  revisions?: Array<{ version: number; at?: string; author?: string; change_type?: string; summary?: string }>;
  decision_history?: Array<{ decided_at?: string; sme_id?: string; assessment?: string; decision_gate?: string;
                            comment?: string; choice?: string; reviewed_version?: number }>;
  tags?: Record<string, any>;
  _raw?: any; [k: string]: any;
}

// SME decision taxonomy — mirrors backend dq/services/sme_feedback_taxonomy.py (single source).
export type Gate = "APPROVED" | "CONDITIONAL" | "PENDING" | "REJECTED";
export interface AssessmentDef { value: string; gate: Gate; label: string; choice: string; condition?: string; }
export const SME_ASSESSMENTS: AssessmentDef[] = [
  { value: "APPROVED_AS_IS",              gate: "APPROVED",    label: "Approve — makes sense",                choice: "confirm_authority" },
  { value: "CONDITIONAL_CORRECTION",      gate: "CONDITIONAL", label: "Conditional — needs correction",       choice: "conditional_approve", condition: "correction applied" },
  { value: "CONDITIONAL_GOVERNANCE",      gate: "CONDITIONAL", label: "Conditional — pending governed values", choice: "conditional_approve", condition: "governed values bound" },
  { value: "CONDITIONAL_APPLICABILITY",   gate: "CONDITIONAL", label: "Conditional — confirm applicability",   choice: "conditional_approve", condition: "applicability confirmed" },
  { value: "NEEDS_BUSINESS_CONFIRMATION", gate: "PENDING",     label: "Needs business confirmation",          choice: "defer" },
  { value: "REJECTED_INVALID",            gate: "REJECTED",    label: "Reject — does not make sense",          choice: "reject_rule" },
  { value: "REJECTED_DUPLICATE",          gate: "REJECTED",    label: "Reject — duplicate",                    choice: "reject_rule" },
  { value: "OUT_OF_SCOPE",                gate: "REJECTED",    label: "Skip — out of scope",                   choice: "skip" },
];
export const ASSESSMENT_BY_VALUE: Record<string, AssessmentDef> =
  Object.fromEntries(SME_ASSESSMENTS.map((a) => [a.value, a]));
export const GATE_TONE: Record<Gate, string> =
  { APPROVED: "#2f7a4f", CONDITIONAL: "#946011", PENDING: "#43607e", REJECTED: "#b23a30" };

/** Load the decision vocabulary from the backend (single source of truth:
 *  dq/services/sme_feedback_taxonomy.py). Falls back to the constants above if the call fails,
 *  so the console still works against an older backend. */
export async function sdh_smeTaxonomy(): Promise<AssessmentDef[]> {
  try {
    const res: any = await apiGet(`${BASE}/taxonomy/sme-decisions`);
    const list = (res?.assessments || []).map((a: any) => ({
      value: a.value, gate: a.gate as Gate, label: a.label, choice: a.choice, condition: a.condition,
    }));
    return list.length ? list : SME_ASSESSMENTS;
  } catch { return SME_ASSESSMENTS; }
}
export interface RulesResponse {
  success: boolean; entity_id: string; ruleset_version?: string;
  counts: Record<Lifecycle, number>; rules: EnrichedRule[];
}

export interface RuleResult {
  control_id?: string; family?: string; enforcement?: string; target_table?: string;
  total_rows?: number; applicable_rows?: number; violating_rows?: number;
  pass_rate?: number; status?: string; error?: string;
}
export interface Conformance {
  success: boolean; entity_id: string;
  conformance?: any; rule_results: RuleResult[]; mode?: string; generated_at?: string;
}
export interface ConformanceRun {
  run_id: string; ruleset_version?: string; generated_at?: string;
  data_source?: string; mode?: string; compiled_count?: number; conformance?: any;
}

export interface ProfileTable { table: string; columns: number; records?: number; mandatory: number; avg_null: number; }
export interface ProfileColumn {
  table: string; column: string; data_type?: string; records?: number;
  distinct: number; null_pct: number; mandatory: boolean; numeric_stats?: any;
}
export interface ProfileColumnDetail {
  success: boolean; table: string; column: string; data_type?: string; records?: number;
  null_pct: number; distinct: number; numeric_stats?: any;
  top_values: { value: string; count: number }[];
}

// ── Review ──
/** SME bulk review via the exported workbook — dry-run preview (writes nothing). */
export interface ImportChange {
  excel_row: number; rule_id: string; rule_name?: string; field?: string;
  from_gate: string; from_assessment?: string | null;
  to_gate: string; to_assessment: string; to_label?: string;
  comment: string; comment_missing?: boolean; version?: number;
}
export interface ImportPreview {
  ok: boolean; sheet?: string; missing_columns?: string[]; message?: string;
  stats?: { rows_in_file: number; with_decision: number; will_change: number; unchanged: number;
            errors: number; warnings: number; missing_comments: number };
  by_transition?: Array<{ key: string; from_gate: string; to_gate: string; count: number; rule_ids: string[] }>;
  by_new_gate?: Record<string, number>; by_new_assessment?: Record<string, number>;
  by_old_gate?: Record<string, number>;
  changes?: ImportChange[]; unchanged?: ImportChange[];
  errors?: Array<{ excel_row: number; rule_id?: string; error: string }>;
  warnings?: Array<{ excel_row: number; rule_id?: string; warning: string }>;
}
export async function sdh_importPreview(entityId: string, appNm: string, file: File): Promise<ImportPreview> {
  const fd = new FormData(); fd.append("app_nm", appNm); fd.append("file", file);
  const res: any = await apiClient.post(`${BASE}/${encodeURIComponent(entityId)}/sme-import/preview`, fd);
  return (res?.data?.result ?? res?.result ?? res) as ImportPreview;
}
export async function sdh_importApply(entityId: string, appNm: string, file: File,
  smeId: string, smeName?: string, ruleIds?: string[]): Promise<any> {
  const fd = new FormData();
  fd.append("app_nm", appNm); fd.append("file", file); fd.append("sme_id", smeId);
  if (smeName) fd.append("sme_name", smeName);
  if (ruleIds?.length) fd.append("rule_ids", ruleIds.join(","));
  const res: any = await apiClient.post(`${BASE}/${encodeURIComponent(entityId)}/sme-import/apply`, fd);
  return res?.data?.result ?? res?.result ?? res;
}

/** SME feedback loop (pass 3 / iteration 2). */
export interface RefineOption {
  label: string; action: "REVISE" | "KEEP" | "DROP"; recommended?: boolean;
  revised?: { constraint_family?: string; condition?: string; predicate_template?: string;
              expected_outcome?: string; business_logic?: string; severity?: string };
  rationale?: string; trade_off?: string; confidence?: number;
  /** true when the proposal still contains a <placeholder> the SME must fill in */
  needs_input?: boolean;
  /** what the SME must do outside the rule for this option to work (e.g. load a governed domain) */
  sme_action?: string | null;
}
export interface RefineProposal {
  action: "REVISE" | "KEEP" | "DROP";
  revised?: Record<string, any>;
  explanation?: string; addresses_feedback?: boolean; confidence?: number;
  interpretation?: string; challenge?: string | null;
  question_for_sme?: string | null; options?: RefineOption[];
}
export interface RefineResult {
  status: string; constraint_id?: string; proposal?: RefineProposal;
  options?: RefineOption[]; interpretation?: string; challenge?: string | null;
  question_for_sme?: string | null;
  before?: Record<string, any>; after?: Record<string, any>;
  persisted?: boolean; version?: number;
  reason?: string; diagnostics?: any[];
}
/** Runtime "rule studio": revise ONE rule from a live directive. persist=false → propose only. */
export interface RefineTurn { role: "sme" | "agent"; content: string }
export async function sdh_refineInteractive(entityId: string, appNm: string, constraintId: string,
  smeDirective?: string, persist = false, iteration = 2, chosenOption?: string,
  conversation?: RefineTurn[], proposal?: RefineProposal): Promise<RefineResult> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/interactive`,
    { app_nm: appNm, entity_id: entityId, constraint_id: constraintId,
      sme_directive: smeDirective, persist, iteration, chosen_option: chosenOption,
      conversation,
      // On Save we send the proposal already on screen so the backend APPLIES it instead of
      // re-running the agent (which cost a call and could return something else).
      proposal });
  return (res?.result ?? res) as RefineResult;
}
export interface RuleVersion {
  version: number; at?: string; author?: string; change_type?: string; summary?: string;
  is_active?: boolean; restorable?: boolean;
  snapshot?: Record<string, any>;
  impact?: { executed?: boolean | null; status?: string; applicable_rows?: number; violations?: number; note?: string } | null;
  diff?: Record<string, { from?: any; to?: any }>;
}
export async function sdh_ruleVersions(entityId: string, constraintId: string):
  Promise<{ active_version: number; versions: RuleVersion[] }> {
  const res: any = await apiGet(
    `${BASE}/${encodeURIComponent(entityId)}/rule/${encodeURIComponent(constraintId)}/versions`);
  return res?.result ?? res;
}
/** Make an earlier version the active rule (append-only — recorded as a new version). */
export async function sdh_activateVersion(entityId: string, appNm: string, constraintId: string,
  version: number, actor?: string): Promise<any> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/rule/activate-version`,
    { app_nm: appNm, constraint_id: constraintId, version, actor, remeasure: true });
  return res?.result ?? res;
}

export interface OptionImpact {
  label?: string; action?: string; status?: string;
  applicable?: number | null; violating?: number | null;
  violation_rate_pct?: number | null; pass_rate_pct?: number | null;
  sample_failing?: any[]; sample_passing?: any[]; note?: string;
}
/** Execute every proposed option on sample data — impact per option, nothing persisted. */
export async function sdh_previewOptions(entityId: string, appNm: string, constraintId: string,
  options: any[]): Promise<{ constraint_id: string; options: OptionImpact[] }> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/preview-options`,
    { app_nm: appNm, constraint_id: constraintId, options });
  return res?.result ?? res;
}
export async function sdh_remeasureRule(entityId: string, appNm: string, constraintId: string): Promise<any> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/remeasure`,
    { app_nm: appNm, constraint_id: constraintId });
  return res?.result ?? res;
}
/** Discard an agent refinement and restore the previous rule (recorded as a new revision). */
export async function sdh_revertRefinement(entityId: string, appNm: string, constraintId: string,
  actor?: string): Promise<any> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/revert`,
    { app_nm: appNm, constraint_id: constraintId, actor });
  return res?.result ?? res;
}

export interface RefineCandidates {
  total_refinable: number;
  by_gate?: Record<string, number>;
  by_assessment?: Record<string, number>;
  last_request?: any;
}
export async function sdh_refineCandidates(entityId: string): Promise<RefineCandidates> {
  const res: any = await apiGet(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/candidates`);
  return (res?.result ?? res) as RefineCandidates;
}
/** Save WHAT to refine. Running it is a pipeline JOB (DQ_SME_REFINEMENT) — never inline. */
export async function sdh_refineRequest(entityId: string, appNm: string, gates?: string[],
  assessments?: string[], ruleIds?: string[], requestedBy?: string): Promise<any> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/request`,
    { app_nm: appNm, entity_id: entityId, gates, assessments, rule_ids: ruleIds,
      iteration: 2, requested_by: requestedBy });
  return res?.result ?? res;
}
/** Submit the refinement as a pipeline job so the API stays responsive.
 *  Reuses the existing adriva_runPipeline helper — the route is
 *  POST /adriva_api/pipeline/{process_id|"new"}/run, not /pipeline/run. */
export async function sdh_runRefinementJob(_sessionId: string, appNm: string, entityId: string): Promise<any> {
  const { adriva_runPipeline } = await import("../API");
  return adriva_runPipeline(appNm, entityId, "", ["DQ_SME_REFINEMENT"], false);
}

export async function sdh_refineRun(entityId: string, appNm: string,
  assessments?: string[], ruleIds?: string[], iteration = 2): Promise<any> {
  const res: any = await apiPost(`${BASE}/${encodeURIComponent(entityId)}/sme-refine/run`,
    { app_nm: appNm, entity_id: entityId, assessments, rule_ids: ruleIds, iteration });
  return res?.result ?? res;
}
export interface IterationItem {
  constraint_id: string; rule_name?: string; field?: string; action?: string;
  explanation?: string; sme_comment?: string; assessment?: string; version?: number;
  review_status?: string; changes?: Record<string, { from?: any; to?: any }>;
}
export interface IterationReport {
  iteration: number; total: number; counts?: Record<string, number>;
  by_assessment?: Record<string, number>; items?: IterationItem[];
}
export async function sdh_iterationReport(entityId: string, iteration = 2): Promise<IterationReport> {
  const res: any = await apiGet(`${BASE}/${encodeURIComponent(entityId)}/iteration/${iteration}`);
  return (res?.result ?? res) as IterationReport;
}

/** Everything known about one field's values — profile, pipeline-inferred, client-supplied, canonical. */
export interface FieldValues {
  table: string; column: string;
  grain?: { grain?: string; note?: string } | null;
  observed?: { distinct_count?: number; null_pct?: number; data_type?: string;
               values?: Array<{ value: string; rows: number; pct?: number }> } | null;
  step1_profiled?: { entity_name?: string; status?: string; reviewed_by?: string; reviewed_at?: string;
                     values?: string[]; rationale?: string } | null;
  step2_client_supplied?: { mapped_logical_field?: string; domain_present_in_client_extract?: boolean;
                            codes_in_client_domain?: number; has_status_information?: boolean;
                            obsolete_codes?: string[];
                            values?: Array<{ code: string; label?: string; status?: string }> } | null;
  step3_canonical?: { status?: string; values?: any[] } | null;
  reconciliation?: string[];
}
export async function sdh_fieldValues(entityId: string, table: string, column: string): Promise<FieldValues> {
  const res: any = await apiGet(
    `${BASE}/${encodeURIComponent(entityId)}/field-values/${encodeURIComponent(table)}/${encodeURIComponent(column)}`);
  return (res?.result ?? res) as FieldValues;
}

export function sdh_reviewQueue(entityId: string): Promise<ReviewQueue> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/review-queue`);
}
export function sdh_applyDecisions(entityId: string, appNm: string, decisions: any[]): Promise<any> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/apply-decisions`, { app_nm: appNm, decisions });
}
export function sdh_preview(entityId: string, appNm: string, constraintIds?: string[], which = "enforceable"): Promise<any> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/preview`, { app_nm: appNm, constraint_ids: constraintIds ?? null, which });
}

// ── Rules ──
export interface CompletenessFinding {
  field_id: string; table: string; column: string; null_pct: number; finding: string; label?: string;
}
export function sdh_completeness(entityId: string): Promise<{ success: boolean; count: number; findings: CompletenessFinding[] }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/completeness`);
}
export function sdh_rules(entityId: string, lifecycle: Lifecycle = "All"): Promise<RulesResponse> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/rules?lifecycle=${lifecycle}`);
}
export function sdh_ruleDetail(entityId: string, ruleId: string): Promise<{ success: boolean; rule: EnrichedRule }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/rule/${encodeURIComponent(ruleId)}`);
}
export function sdh_finalRules(entityId: string): Promise<RulesResponse & { approved_count?: number }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/final-rules`);
}

// ── Results ──
export function sdh_conformance(entityId: string): Promise<Conformance> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/conformance`);
}
export function sdh_conformanceHistory(entityId: string): Promise<{ success: boolean; runs: ConformanceRun[] }> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/conformance-history`);
}

// ── Data profile ──
export function sdh_dataProfile(entityId: string, table?: string): Promise<{ success: boolean; tables: ProfileTable[]; columns: ProfileColumn[] }> {
  const q = table ? `?table=${encodeURIComponent(table)}` : "";
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/data-profile${q}`);
}
export function sdh_dataProfileColumn(entityId: string, table: string, column: string): Promise<ProfileColumnDetail> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/data-profile/${encodeURIComponent(table)}/${encodeURIComponent(column)}`);
}

// ── Export (download v2 workbook) ──
export async function sdh_exportRulebook(entityId: string, appNm: string): Promise<void> {
  const res = await apiClient.post(
    `${BASE}/${encodeURIComponent(entityId)}/export?app_nm=${encodeURIComponent(appNm)}`,
    {}, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = `${entityId}_DQ_Rules_SME.xlsx`;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ── Field-centric compact view ──
export interface FieldRule {
  rule_id: string; rule_name: string; family: string; category: string; dq_dimension: string;
  lifecycle: string; approval_status?: string; severity?: string; confidence?: number;
  business_logic?: string; description?: string; owner_field?: string;
  impact_violations?: number | null; impact_pass_rate?: number | null; impact_status?: string | null;
  business_control_id?: string; [k: string]: any;
}
export interface FieldRow {
  table: string; column: string; data_type?: string; records?: number; distinct: number;
  null_pct: number; mandatory: boolean; numeric_stats?: any;
  top_values: { value: string; count: number }[];
  rule_counts: { Approved: number; Draft: number; Rejected: number };
  rules: FieldRule[];
  execution: { rules_run: number; violations: number; any_fail: boolean };
}
export interface FieldView {
  success: boolean; entity_id: string;
  tables: { table: string; columns: number }[]; fields: FieldRow[];
}
export function sdh_fieldView(entityId: string, table?: string): Promise<FieldView> {
  const q = table ? `?table=${encodeURIComponent(table)}` : "";
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/field-view${q}`);
}
